import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, Quote, ChevronLeft, ChevronRight, ArrowRight, Phone } from "lucide-react";
import { TESTIMONIALS, SITE } from "../lib/site";
import QRCodeReview from "../components/QRCodeReview";

export default function Reviews() {
  const [idx, setIdx] = useState(0);
  const current = TESTIMONIALS[idx];

  const prev = () => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const next = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);

  return (
    <>
      <section className="section pt-16 md:pt-24" data-testid="reviews-hero">
        <div className="container-luxury">
          <p className="overline">Customer Reviews</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl text-white font-light leading-[1.05]">
            What our <span className="font-script text-[var(--gold)]">neighbors</span> say
          </h1>

          {/* Star summary */}
          <div className="mt-10 flex items-center gap-8 flex-wrap" data-testid="reviews-summary">
            <div>
              <p className="font-display text-6xl text-white">5.0</p>
              <div className="mt-2 flex gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={18} fill="#C5A059" className="text-[var(--gold)]" />
                ))}
              </div>
              <p className="mt-2 text-white/50 text-sm uppercase tracking-[0.2em]">Average Rating</p>
            </div>
            <div className="border-l border-white/10 pl-8">
              <p className="font-display text-3xl text-white">{TESTIMONIALS.length}+</p>
              <p className="mt-2 text-white/50 text-sm uppercase tracking-[0.2em]">Verified Testimonials</p>
            </div>
            <div className="border-l border-white/10 pl-8">
              <p className="font-display text-3xl text-white">10 Yrs</p>
              <p className="mt-2 text-white/50 text-sm uppercase tracking-[0.2em]">Of Excellence</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial slider */}
      <section className="pb-24" data-testid="reviews-slider-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center bg-[#0c0c0c] border border-white/5 p-8 md:p-16">
            <div className="lg:col-span-8" data-testid="testimonial-slider">
              <Quote size={48} className="text-[var(--gold)] opacity-60" />
              <p className="mt-6 font-display text-2xl md:text-3xl text-white italic leading-snug min-h-[140px]">
                "{current.quote}"
              </p>
              <div className="mt-8 flex gap-1">
                {Array.from({ length: current.rating }).map((_, i) => (
                  <Star key={i} size={16} fill="#C5A059" className="text-[var(--gold)]" />
                ))}
              </div>
              <p className="mt-4 text-white uppercase tracking-[0.2em] text-sm">{current.name}</p>
              <p className="text-white/50 text-sm">{current.location} · {current.service}</p>

              <div className="mt-10 flex items-center gap-4">
                <button onClick={prev} className="btn-ghost !px-4 !py-3" aria-label="Previous" data-testid="testimonial-prev">
                  <ChevronLeft size={18} />
                </button>
                <button onClick={next} className="btn-ghost !px-4 !py-3" aria-label="Next" data-testid="testimonial-next">
                  <ChevronRight size={18} />
                </button>
                <span className="ml-4 text-white/40 text-xs uppercase tracking-[0.2em]">
                  {idx + 1} / {TESTIMONIALS.length}
                </span>
              </div>
            </div>

            <div className="lg:col-span-4 lg:border-l lg:border-white/5 lg:pl-12">
              <QRCodeReview />
            </div>
          </div>

          {/* Grid of all testimonials */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-[#0A0A0A] p-8" data-testid={`testimonial-card-${i}`}>
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} fill="#C5A059" className="text-[var(--gold)]" />
                  ))}
                </div>
                <p className="mt-5 text-white/80 leading-relaxed italic">"{t.quote}"</p>
                <p className="mt-6 text-white uppercase tracking-[0.2em] text-xs">{t.name}</p>
                <p className="text-white/50 text-xs">{t.location} · {t.service}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#0c0c0c]" data-testid="reviews-cta">
        <div className="container-luxury text-center">
          <h2 className="font-display text-4xl md:text-5xl text-white font-light">
            Loved our work? <br />
            <span className="font-script text-[var(--gold)]">Tell the neighborhood.</span>
          </h2>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a href={SITE.googleReviewUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="reviews-leave-cta">
              <Star size={16} /> Leave a Google Review
            </a>
            <Link to="/contact" className="btn-secondary" data-testid="reviews-estimate-cta">
              Get Free Estimate <ArrowRight size={16} />
            </Link>
            <a href={SITE.phoneHref} className="btn-ghost" data-testid="reviews-call-cta">
              <Phone size={16} /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
